import * as React from "react";
import { Input, InputChangeEvent } from "@progress/kendo-react-inputs";
import { useJobSetupStore } from "@presentation/stores/JobSetupStore";
import { useTranslation } from "react-i18next";
import { FieldWrapper } from "@progress/kendo-react-form";

const JOB_NAME_REGEX = /^(([A-Za-z0-9][-A-Za-z0-9_.]*)?[A-Za-z0-9])?$/;
const JOB_NAME_MAX_LEN = 63;
const COMBINED_MAX_LEN = 62;

const ErrorMessage: React.FC<{ id?: string; children: React.ReactNode }> = ({ id, children }) => (
  <div id={id} className="k-form-error">
    {children}
  </div>
);

export const NamingFormBlock: React.FC = () => {
  const { t: loc } = useTranslation();

  const { name, setName, analysisName, setAnalysisName, showValidation } = useJobSetupStore((state) => ({
    name: state.CreateJobState.name,
    setName: state.setName,
    analysisName: state.CreateJobState.analysisName,
    setAnalysisName: state.setAnalysisName,
    showValidation: state.showValidation,
  }));

  const [touched, setTouched] = React.useState<boolean>(false);
  const [analysisTouched, setAnalysisTouched] = React.useState<boolean>(false);
  const errorId = React.useId();
  const analysisErrorId = React.useId();

  const combinedLength = (name ?? "").trim().length + (analysisName ?? "").trim().length;

  const error: string = React.useMemo(() => {
    const v = (name ?? "").trim();
    if (v.length === 0) return "REQUIRED_HIDDEN";
    if (v.length > JOB_NAME_MAX_LEN) return loc("maxLength", { max: JOB_NAME_MAX_LEN });
    if (combinedLength > COMBINED_MAX_LEN) return loc("maxLength", { max: COMBINED_MAX_LEN });
    if (!JOB_NAME_REGEX.test(v)) return loc("invalidProjectNameFormat");
    return "";
  }, [name, combinedLength, loc]);

  const analysisError: string = React.useMemo(() => {
    const v = (analysisName ?? "").trim();
    if (v.length === 0) return "";
    if (v.length > JOB_NAME_MAX_LEN) return loc("maxLength", { max: JOB_NAME_MAX_LEN });
    if (combinedLength > COMBINED_MAX_LEN) return loc("maxLength", { max: COMBINED_MAX_LEN });
    if (!JOB_NAME_REGEX.test(v)) return loc("invalidProjectNameFormat");
    return "";
  }, [analysisName, combinedLength, loc]);

  const showError = error !== "";
  const showAnalysisError = (analysisTouched || showValidation) && analysisError !== "";

  const handleProjectNameChange = (e: InputChangeEvent) => {
    if (!touched) setTouched(true);
    setName((e.value ?? "") as string);
  };

  const handleProjectNameBlur: React.FocusEventHandler<HTMLInputElement> = () => {
    if (!touched) setTouched(true);
  };

  const handleAnalysisNameChange = (e: InputChangeEvent) => {
    if (!analysisTouched) setAnalysisTouched(true);
    setAnalysisName((e.value ?? "") as string);
  };

  const handleAnalysisNameBlur: React.FocusEventHandler<HTMLInputElement> = () => {
    if (!analysisTouched) setAnalysisTouched(true);
  };

  const blockSpace: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === " ") e.preventDefault();
  };

  return (
    <fieldset className="k-form-fieldset">
      <legend className="k-form-legend" style={{ marginBottom: "-20px" }}>
        <strong>Naming</strong>
      </legend>

      <FieldWrapper style={{ marginBottom: "-10px" }}>
        <Input
          label={loc("projectName")}
          placeholder="e.g. companyName"
          labelClassName="k-form-label"
          value={name ?? ""}
          valid={!showError}
          required
          onChange={handleProjectNameChange}
          onBlur={handleProjectNameBlur}
          onKeyDown={blockSpace}
          aria-invalid={showError}
          ariaDescribedBy={showError ? errorId : undefined}
        />

        {showError && error !== "REQUIRED_HIDDEN" && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
      </FieldWrapper>

      <FieldWrapper>
        <Input
          label="Analysis Name"
          placeholder="e.g. R01, R02"
          labelClassName="k-form-label"
          value={analysisName ?? ""}
          valid={!showAnalysisError}
          onChange={handleAnalysisNameChange}
          onBlur={handleAnalysisNameBlur}
          onKeyDown={blockSpace}
          aria-invalid={showAnalysisError}
          ariaDescribedBy={showAnalysisError ? analysisErrorId : undefined}
        />

        {showAnalysisError && <ErrorMessage id={analysisErrorId}>{analysisError}</ErrorMessage>}
      </FieldWrapper>
    </fieldset>
  );
};
